import { useEffect } from "react";

// Minimal Core Web Vitals beacon — LCP and CLS — without a library dependency.
// Values are flushed to /analytics/web_vitals on page visibility change.
// Tracked only in browsers that support PerformanceObserver for the relevant entry types.

type VitalsSample = { path: string; lcp?: number; cls?: number };

export function useWebVitals() {
  useEffect(() => {
    if (typeof PerformanceObserver === "undefined") return;

    const sample: VitalsSample = { path: window.location.pathname };
    let sent = false;

    const observers: PerformanceObserver[] = [];

    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const last = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
        if (last) sample.lcp = Math.round(last.startTime);
      });
      lcpObserver.observe({ type: "largest-contentful-paint", buffered: true } as PerformanceObserverInit);
      observers.push(lcpObserver);
    } catch { /* unsupported entry type */ }

    try {
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries() as Array<PerformanceEntry & { value: number; hadRecentInput?: boolean }>) {
          if (!entry.hadRecentInput) clsValue += entry.value;
        }
        sample.cls = Math.round(clsValue * 1000) / 1000;
      });
      clsObserver.observe({ type: "layout-shift", buffered: true } as PerformanceObserverInit);
      observers.push(clsObserver);
    } catch { /* unsupported entry type */ }

    const flush = () => {
      if (sent) return;
      if (sample.lcp === undefined && sample.cls === undefined) return;
      sent = true;
      const body = JSON.stringify(sample);
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon("/analytics/web_vitals", new Blob([body], { type: "application/json" }));
        } else {
          fetch("/analytics/web_vitals", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: true,
          }).catch(() => { /* swallow */ });
        }
      } catch { /* swallow */ }
    };

    const onVisibilityChange = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", flush);

    return () => {
      observers.forEach((o) => o.disconnect());
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", flush);
    };
  }, []);
}
